import React, { useState } from 'react';
import { CalendarEvent } from '../../../types/calendar';
import { Order, OrderStatus } from '../../../types/order';
import { TodoTask } from '../../../types/todo';
import { formatDateForDisplay, formatTime, sortEventsByTime } from '../../../utils/calendarUtils';
import { getDaysSinceOrder, getOrderRowBackgroundColor } from '../../../utils/orderUtils';
import { getTaskBackgroundColor, getTaskBorderColor } from '../../../utils/taskColorUtils';
import { useAppDispatch } from '../../../store/hooks';
import { deleteTask, completeTaskWithBatch } from '../../../store/slices/todoSlice';
import { toggleEventCompletion } from '../../../store/slices/calendarSlice';
import DeleteTaskConfirmationModal from './DeleteTaskConfirmationModal';

const COMPLETION_KEY_PREFIX = 'delivery_completion_';

// Helper functions for localStorage
const getCompletionState = (eventId: string): boolean => {
  const stored = localStorage.getItem(`${COMPLETION_KEY_PREFIX}${eventId}`);
  return stored === 'true';
};

const setCompletionState = (eventId: string, isCompleted: boolean): void => {
  localStorage.setItem(`${COMPLETION_KEY_PREFIX}${eventId}`, String(isCompleted));
  // Dispatch custom event to notify other components
  window.dispatchEvent(
    new CustomEvent('deliveryCompletionChange', {
      detail: { eventId, isCompleted }
    })
  );
};

interface DayDetailsModalProps {
  isOpen: boolean;
  selectedDate: string;
  events: CalendarEvent[];
  orders: Order[];
  tasks: TodoTask[];
  onClose: () => void;
}

const DayDetailsModal: React.FC<DayDetailsModalProps> = ({ isOpen, selectedDate, events, orders, tasks, onClose }) => {
  const dispatch = useAppDispatch();
  const [completingTasks, setCompletingTasks] = useState<Set<string>>(new Set());
  const [taskToDelete, setTaskToDelete] = useState<{ id: string; title: string } | null>(null);
  const [deliveryCompletions, setDeliveryCompletions] = useState<Record<string, boolean>>(() => {
    // Initialize completion states for all delivery events
    const initialStates: Record<string, boolean> = {};
    events.forEach(event => {
      if (event.title.startsWith('Delivery:')) {
        initialStates[event.id] = getCompletionState(event.id);
      }
    });
    return initialStates;
  });

  // Listen for completion changes from other components (like EventCard in calendar view)
  React.useEffect(() => {
    const handleCompletionChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ eventId: string; isCompleted: boolean }>;
      setDeliveryCompletions(prev => ({
        ...prev,
        [customEvent.detail.eventId]: customEvent.detail.isCompleted
      }));
    };

    window.addEventListener('deliveryCompletionChange', handleCompletionChange);
    return () => {
      window.removeEventListener('deliveryCompletionChange', handleCompletionChange);
    };
  }, []);

  if (!isOpen) return null;

  // Filter orders to only show those matching the selected date
  const filteredOrders = orders.filter(order => order.orderDate === selectedDate);

  const allEvents = sortEventsByTime(events);

  // Sort events: incomplete first, completed last (for deliveries)
  const sortedEvents = allEvents.sort((a, b) => {
    const aCompleted = deliveryCompletions[a.id] || false;
    const bCompleted = deliveryCompletions[b.id] || false;
    return (aCompleted ? 1 : 0) - (bCompleted ? 1 : 0);
  });

  // Sort tasks: incomplete first, completed last
  const sortedTasks = [...tasks].sort((a, b) => {
    const aCompleted = a.status === 'completed' ? 1 : 0;
    const bCompleted = b.status === 'completed' ? 1 : 0;
    return aCompleted - bCompleted;
  });

  const hasEvents = sortedEvents.length > 0;
  const hasOrders = filteredOrders.length > 0;
  const hasTasks = sortedTasks.length > 0;

  const getStatusColor = (status: OrderStatus): string => {
    switch (status) {
      case 'Completed':
        return '#4CAF50';
      case 'In Progress':
        return '#FF9800';
      case 'Pending':
      default:
        return '#2196F3';
    }
  };

  const handleRemoveTask = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const task = tasks.find(t => t.id === taskId);
    if (task?.status === 'completed') return;

    // Show confirmation modal instead of directly deleting
    setTaskToDelete({ id: taskId, title: task?.title || 'Untitled task' });
  };

  const handleConfirmDelete = () => {
    if (taskToDelete) {
      dispatch(deleteTask(taskToDelete.id));
      setTaskToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setTaskToDelete(null);
  };

  const isFillingPrepTask = (task: TodoTask): boolean => {
    return task.taskType === 'batch' && !!(task.fillingName && task.batchesNeeded !== undefined);
  };

  const isOrderFulfillmentTask = (task: TodoTask): boolean => {
    return task.taskType === 'fulfillment' && !!(task.fillingName && task.batchesNeeded !== undefined);
  };

  const isCompletableTask = (task: TodoTask): boolean => {
    return isFillingPrepTask(task) || isOrderFulfillmentTask(task);
  };

  const handleDeliveryCheckboxChange = (eventId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const newState = e.target.checked;

    // Dispatch Redux action to trigger middleware
    dispatch(toggleEventCompletion(eventId));

    // Update local state and localStorage for immediate UI feedback
    setDeliveryCompletions(prev => ({ ...prev, [eventId]: newState }));
    setCompletionState(eventId, newState);
  };

  const handleCheckboxClick = (task: TodoTask, e: React.MouseEvent) => {
    e.stopPropagation();

    // Prevent interaction with completed tasks
    if (task.status === 'completed') return;

    // Only handle filling prep and order fulfillment tasks
    if (!isCompletableTask(task)) return;

    // Mark task as completing (for animation)
    setCompletingTasks(prev => new Set(prev).add(task.id));

    // Dispatch action to complete task and record batch
    dispatch(completeTaskWithBatch({ taskId: task.id }));

    // After animation, clear the completing state
    setTimeout(() => {
      // Task remains in store with status='completed'
      // This allows DayPlanner to track it in fulfilledOrdersByFilling
      setCompletingTasks(prev => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }, 600); // Match animation duration
  };

  return (
    <>
      <style>{`
        .filling-prep-task:hover {
          box-shadow: 0 2px 8px rgba(245, 127, 23, 0.3);
          transform: translateY(-1px);
        }
        .filling-prep-task .delete-task-button:hover {
          background-color: #d32f2f;
          color: white;
          transform: scale(1.1);
        }

        /* Checkbox animation */
        .task-checkbox {
          cursor: pointer;
          transition: all 0.2s ease;
          user-select: none;
        }
        .task-checkbox:hover {
          transform: scale(1.2);
        }

        /* Completion animation */
        @keyframes taskComplete {
          0% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
            background-color: #4CAF50;
          }
          100% {
            opacity: 0;
            transform: scale(0.95) translateX(20px);
          }
        }

        .task-completing {
          animation: taskComplete 0.6s ease-out forwards;
        }

        /* Checkmark animation */
        @keyframes checkmarkPop {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.3);
          }
          100% {
            transform: scale(1);
          }
        }

        .checkmark-animated {
          animation: checkmarkPop 0.3s ease-out;
        }

        /* Disable hover effects for completed tasks */
        .task-completed.filling-prep-task:hover {
          box-shadow: none;
          transform: none;
        }

        .task-completed .task-checkbox:hover {
          transform: none;
          cursor: default;
        }

        /* Prevent completion animation on already-completed tasks */
        .task-completed.task-completing {
          animation: none;
        }
      `}</style>
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>{formatDateForDisplay(selectedDate)}</h2>
            <button style={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>

          <div style={styles.content}>
            {!hasEvents && !hasOrders && !hasTasks && (
              <div style={styles.emptyState}>No events, orders, or tasks for this date</div>
            )}

            {hasOrders && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Orders ({filteredOrders.length})</h3>
                <div style={styles.ordersContainer}>
                  {filteredOrders.map(order => {
                    const daysSince = getDaysSinceOrder(order.orderDate);
                    return (
                      <div
                        key={order.id}
                        style={{
                          ...styles.orderItem,
                          backgroundColor: getOrderRowBackgroundColor(daysSince, order.status)
                        }}
                      >
                        <div style={styles.orderCustomer}>{order.customerName}</div>
                        <div
                          style={{
                            ...styles.orderStatus,
                            color: getStatusColor(order.status)
                          }}
                        >
                          {order.status}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {hasEvents && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Events ({sortedEvents.length})</h3>
                <div style={styles.eventsContainer}>
                  {sortedEvents.map(event => {
                    const isDeliveryEvent = event.title.startsWith('Delivery:');
                    const isCompleted = deliveryCompletions[event.id] || false;

                    return (
                      <div
                        key={event.id}
                        style={{
                          ...styles.eventItem,
                          borderLeftColor: event.color || '#2196F3',
                          ...(isCompleted && styles.eventItemCompleted)
                        }}
                      >
                        <div style={styles.eventHeader}>
                          {isDeliveryEvent && (
                            <input
                              type="checkbox"
                              checked={isCompleted}
                              onChange={e => handleDeliveryCheckboxChange(event.id, e)}
                              onClick={e => e.stopPropagation()}
                              style={styles.deliveryCheckbox}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={styles.eventTime}>
                              {event.allDay ? (
                                <span style={styles.allDayBadge}>All Day</span>
                              ) : event.startTime ? (
                                <span style={styles.timeBadge}>
                                  {formatTime(event.startTime)}
                                  {event.endTime && ` - ${formatTime(event.endTime)}`}
                                </span>
                              ) : null}
                            </div>
                            <div
                              style={{
                                ...styles.eventTitle,
                                ...(isCompleted && styles.eventTextCompleted)
                              }}
                            >
                              {event.title}
                            </div>
                            {event.description && (
                              <div
                                style={{
                                  ...styles.eventDescription,
                                  ...(isCompleted && styles.eventTextCompleted)
                                }}
                              >
                                {event.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {hasTasks && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Tasks ({sortedTasks.length})</h3>
                <div style={styles.tasksContainer}>
                  {sortedTasks.map(task => {
                    const isCompletable = isCompletableTask(task);
                    const isCompleting = completingTasks.has(task.id);
                    const isCompleted = task.status === 'completed';

                    const taskClasses = [
                      isCompletable && !isCompleted ? 'filling-prep-task' : '',
                      isCompleting ? 'task-completing' : '',
                      isCompleted ? 'task-completed' : ''
                    ]
                      .filter(Boolean)
                      .join(' ');

                    // Apply dynamic colors based on order age
                    const taskItemStyle = isCompleted
                      ? { ...styles.taskItem, ...styles.taskItemCompleted }
                      : {
                          ...styles.taskItem,
                          backgroundColor: getTaskBackgroundColor(task, orders),
                          borderColor: getTaskBorderColor(task, orders)
                        };

                    return (
                      <div key={task.id} style={taskItemStyle} className={taskClasses}>
                        <div style={styles.taskHeader}>
                          <div style={styles.taskHeaderLeft}>
                            {isCompletable ? (
                              <span
                                style={{
                                  ...styles.taskCheckbox,
                                  ...(isCompleted ? styles.taskCheckboxCompleted : {})
                                }}
                                className={isCompleted ? '' : 'task-checkbox'}
                                onClick={e => handleCheckboxClick(task, e)}
                                title={isCompleted ? 'Task completed' : 'Complete task'}
                              >
                                {isCompleting ? <span className="checkmark-animated">✓</span> : isCompleted ? '☑' : '☐'}
                              </span>
                            ) : (
                              <span style={styles.taskCheckbox}>{isCompleted ? '☑' : '☐'}</span>
                            )}
                            <span style={styles.taskTitle}>{task.title}</span>
                          </div>
                          {isCompletable && !isCompleted && !isCompleting && (
                            <button
                              onClick={e => handleRemoveTask(task.id, e)}
                              style={styles.removeButton}
                              className="delete-task-button"
                              title="Delete task"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                        {task.description && <div style={styles.taskDescription}>{task.description}</div>}
                        <div style={styles.taskMeta}>
                          {task.priority && (
                            <span
                              style={{
                                ...styles.priorityBadge,
                                ...(task.priority === 'high' || task.priority === 'urgent'
                                  ? { backgroundColor: '#FFEBEE', color: '#C62828' }
                                  : task.priority === 'medium'
                                    ? { backgroundColor: '#FFF3E0', color: '#E65100' }
                                    : { backgroundColor: '#E8F5E9', color: '#2E7D32' })
                              }}
                            >
                              {task.priority}
                            </span>
                          )}
                          {task.status && (
                            <span
                              style={{
                                ...styles.statusBadge,
                                ...(task.status === 'completed'
                                  ? { backgroundColor: '#E8F5E9', color: '#2E7D32' }
                                  : task.status === 'in-progress'
                                    ? { backgroundColor: '#E3F2FD', color: '#1565C0' }
                                    : { backgroundColor: '#FFF3E0', color: '#E65100' })
                              }}
                            >
                              {task.status}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DeleteTaskConfirmationModal
        isOpen={!!taskToDelete}
        taskTitle={taskToDelete?.title || ''}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    padding: '20px'
  },
  section: {
    marginBottom: '24px'
  },
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  ordersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  orderItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '4px'
  },
  orderCustomer: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333'
  },
  orderStatus: {
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 8px',
    borderRadius: '12px',
    backgroundColor: 'white'
  },
  eventsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  eventItem: {
    padding: '12px',
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderLeft: '4px solid',
    borderRadius: '4px'
  },
  eventHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px'
  },
  eventTime: {
    marginBottom: '4px'
  },
  allDayBadge: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: '600',
    color: '#666',
    backgroundColor: '#f0f0f0',
    padding: '2px 6px',
    borderRadius: '3px'
  },
  timeBadge: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: '600',
    color: '#666',
    backgroundColor: '#f0f0f0',
    padding: '2px 6px',
    borderRadius: '3px'
  },
  eventTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '4px'
  },
  eventDescription: {
    fontSize: '12px',
    color: '#666',
    lineHeight: '1.4'
  },
  deliveryCheckbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    flexShrink: 0,
    marginTop: '2px',
    accentColor: '#4CAF50'
  },
  eventItemCompleted: {
    opacity: 0.5
  },
  eventTextCompleted: {
    color: '#999'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    fontSize: '14px',
    color: '#999'
  },
  tasksContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  taskItem: {
    padding: '12px',
    backgroundColor: '#FFF9C4',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#FBC02D',
    borderRadius: '4px',
    transition: 'box-shadow 0.2s, transform 0.2s',
    position: 'relative' as const
  },
  taskHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '4px'
  },
  taskHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#F57F17',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'background-color 0.2s, color 0.2s, transform 0.1s',
    lineHeight: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  taskCheckbox: {
    fontSize: '16px',
    color: '#F57F17',
    flexShrink: 0
  },
  taskTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333'
  },
  taskDescription: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
    marginLeft: '24px',
    lineHeight: '1.4'
  },
  taskMeta: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px'
  },
  priorityBadge: {
    fontSize: '11px',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '12px',
    textTransform: 'capitalize'
  },
  statusBadge: {
    fontSize: '11px',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '12px',
    textTransform: 'capitalize'
  },
  taskItemCompleted: {
    backgroundColor: '#F5F5F5',
    borderColor: '#BDBDBD',
    opacity: 0.65,
    cursor: 'default'
  },
  taskCheckboxCompleted: {
    cursor: 'default',
    color: '#757575'
  }
};

export default DayDetailsModal;
